/**
 * RoleDigestView — Personalized RACI-scoped task briefing
 *
 * Renders four collapsible sections (R / A / C / I), each with urgency-sorted
 * task rows on a shared CSS Grid template for cross-section column alignment.
 *
 * Data comes from useRoleDigestData() which composes Gantt tasks, RACI
 * assignments, issue flags, and digest acknowledgments.
 *
 * Epic: DEV-712, Task: DEV-715
 *
 * ─────────────────────────────────────────────────────────────────────
 * CONSOLIDATION NOTE — DEV-1182 (Engagements Tasks tab, 2026-05-31)
 * ─────────────────────────────────────────────────────────────────────
 * This component is the SOURCE OF TRUTH for the per-task interaction
 * capabilities that the Engagements Tasks tab (DEV-1182) consolidates
 * inline. The inline-row-expansion PATTERN below is not preserved by
 * DEV-1182 — designers are free to redesign per-row UX. However, the
 * CAPABILITIES enumerated in DEV-1182's "Feature preservation" block
 * MUST remain reachable from the new Tasks tab regardless of pattern:
 *
 *   • % complete entry on R-tasks (updatePercent)
 *   • Mark Done on R-tasks
 *   • Flag issue with severity (blocking/warning/informational)
 *   • Acknowledge with optional note on I-tasks (DEV-736)
 *   • Respond with free text on C-tasks (DEV-737, task_consultations —
 *     distinct from comments)
 *   • Blockers list per task (upstream blocker name + owner + %)
 *   • Issue chips per task (severity-colored)
 *   • Assigned-to chip with staleness alert (>3d warn, >7d alert)
 *   • Border color by urgency tier (Done blue overrides all)
 *   • WBS path under task name
 *   • Consultation badge on C-tasks (Input needed / Input provided)
 *   • Change description on I-tasks
 *   • Section badges + counts (R/A/C/I) in header
 *   • Date range presets (today / week / month / all)
 *   • Summary alerts (Overdue / Blocked / Unacknowledged)
 *
 * The 💬 placeholder on line ~450 becomes a real CommentIndicator once
 * DEV-720 Phase 1 ships (DEV-721 schema + DEV-722 REST + DEV-726
 * reactions). It is the explicit wiring point — do not remove the
 * column slot during refactors.
 *
 * Before consolidating this component into DEV-1182, re-read
 * https://life4earthglobal.atlassian.net/browse/DEV-1182 to confirm
 * the preservation list above is still authoritative.
 */

import React, { useState, useCallback, useMemo } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  useRoleDigestData,
  type DigestTask,
  type RaciType,
  type UrgencyTier,
  type DigestSummary,
} from "./useRoleDigestData";
// DEV-963: IssueSeverity isn't re-exported by useRoleDigestData; pull from
// its canonical home in the api layer.
import type { IssueSeverity } from "../../api/issueFlags";
import { CommentIndicator } from "../Comments/CommentIndicator";
import styles from "./RoleDigestView.module.css";

// ── Props ────────────────────────────────────────────────────────

interface RoleDigestViewProps {
  projectGuid: string;
  roleSlotGuids: string[];
  currentUserGuid: string;
  debugMode?: boolean;
}

// ── Date range presets ───────────────────────────────────────────

type DatePreset = "today" | "week" | "month" | "all";

function getDateRange(preset: DatePreset): { start: string; end: string } | null {
  const now = new Date();
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  switch (preset) {
    case "today":
      return { start: fmt(now), end: fmt(now) };
    case "week": {
      const end = new Date(now);
      end.setDate(end.getDate() + 7);
      return { start: fmt(now), end: fmt(end) };
    }
    case "month": {
      const end = new Date(now);
      end.setDate(end.getDate() + 30);
      return { start: fmt(now), end: fmt(end) };
    }
    case "all":
      return null;
  }
}

// ── Due-date display helper ──────────────────────────────────────

function daysUntil(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  return Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

interface DueDateInfo {
  text: string;
  className: string;
}

function dueDateDisplay(dateStr: string | null, t: (key: string) => string, isComplete = false): DueDateInfo {
  const days = daysUntil(dateStr);
  if (days === null) return { text: t("digest.noDate"), className: styles.dueDateNone };
  // Completed tasks: "X days late" in blue; active tasks: "X days overdue" in red
  if (isComplete && days < 0) {
    const absDays = Math.abs(days);
    const text = absDays === 1 ? t("digest.oneDayLate") : t("digest.daysLate").replace("{{n}}", String(absDays));
    return { text, className: styles.dueDateDone };
  }
  if (days < -1) return { text: t("digest.daysOverdue").replace("{{n}}", String(Math.abs(days))), className: styles.dueDateDanger };
  if (days === -1) return { text: t("digest.oneDayOverdue"), className: styles.dueDateDanger };
  if (days === 0) return { text: t("digest.dueToday"), className: styles.dueDateWarn };
  if (days === 1) return { text: t("digest.dueTomorrow"), className: styles.dueDateWarn };
  if (days <= 3) return { text: t("digest.dueInDays").replace("{{n}}", String(days)), className: styles.dueDateWarn };
  return { text: t("digest.dueInDays").replace("{{n}}", String(days)), className: styles.dueDateOk };
}

// ── Urgency dot class mapping ────────────────────────────────────

const URGENCY_DOT_CLASS: Record<UrgencyTier, string> = {
  overdue_blocked: styles.urgencyOverdueBlocked,
  overdue: styles.urgencyOverdue,
  imminent: styles.urgencyImminent,
  upcoming: styles.urgencyUpcoming,
  informational: styles.urgencyInfo,
};

const URGENCY_BORDER_COLOR: Record<UrgencyTier, string> = {
  overdue_blocked: "var(--color-error)",
  overdue: "var(--color-error)",
  imminent: "var(--color-warning)",
  upcoming: "var(--color-success)",
  informational: "var(--text-muted)",
};

/** Completed tasks always get blue (Done) regardless of urgency tier. */
function resolveBorderColor(task: DigestTask): string {
  if (task.percentComplete >= 100) return "var(--info-text)";
  return URGENCY_BORDER_COLOR[task.urgencyTier];
}

// ── Issue severity class mapping ─────────────────────────────────

const ISSUE_CLASS: Record<string, string> = {
  blocking: styles.issueBlocking,
  warning: styles.issueWarning,
  informational: styles.issueInfo,
};

// ── Section config ───────────────────────────────────────────────

interface SectionConfig {
  type: RaciType;
  titleKey: string;
  subtitleKey: string;
  badgeClass: string;
  chipClass: string;
}

const SECTIONS: SectionConfig[] = [
  {
    type: "R",
    titleKey: "digest.sectionR",
    subtitleKey: "digest.subtitleR",
    badgeClass: styles.sectionBadgeR,
    chipClass: styles.summaryChipR,
  },
  {
    type: "A",
    titleKey: "digest.sectionA",
    subtitleKey: "digest.subtitleA",
    badgeClass: styles.sectionBadgeA,
    chipClass: styles.summaryChipA,
  },
  {
    type: "C",
    titleKey: "digest.sectionC",
    subtitleKey: "digest.subtitleC",
    badgeClass: styles.sectionBadgeC,
    chipClass: styles.summaryChipC,
  },
  {
    type: "I",
    titleKey: "digest.sectionI",
    subtitleKey: "digest.subtitleI",
    badgeClass: styles.sectionBadgeI,
    chipClass: styles.summaryChipI,
  },
];

// ═══════════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════════

// ── Summary Strip ────────────────────────────────────────────

interface SummaryStripProps {
  summary: DigestSummary;
  t: (key: string) => string;
}

const SummaryStrip: React.FC<SummaryStripProps> = ({ summary, t }) => (
  <div className={styles.summaryStrip}>
    {SECTIONS.map((sec) => {
      const count = summary[`total${sec.type}` as keyof DigestSummary] as number;
      return (
        <span key={sec.type} className={sec.chipClass}>
          {sec.type} {count}
        </span>
      );
    })}

    {summary.overdue > 0 && (
      <>
        <span className={styles.summaryDivider} />
        <span className={`${styles.summaryAlert} ${styles.summaryAlertOverdue}`}>
          {t("digest.overdue").replace("{{n}}", String(summary.overdue))}
        </span>
      </>
    )}
    {summary.blocked > 0 && (
      <>
        <span className={styles.summaryDivider} />
        <span className={`${styles.summaryAlert} ${styles.summaryAlertBlocked}`}>
          {t("digest.blocked").replace("{{n}}", String(summary.blocked))}
        </span>
      </>
    )}
    {summary.unacknowledged > 0 && (
      <>
        <span className={styles.summaryDivider} />
        <span className={`${styles.summaryAlert} ${styles.summaryAlertUnacked}`}>
          {t("digest.unacknowledged").replace("{{n}}", String(summary.unacknowledged))}
        </span>
      </>
    )}
  </div>
);

// ── Progress Bar ─────────────────────────────────────────────

interface ProgressBarProps {
  percent: number;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ percent }) => {
  let fillClass = styles.progressFillOk;
  if (percent >= 100) fillClass = styles.progressFillDone;
  else if (percent >= 80) fillClass = styles.progressFillWarn;
  return (
    <div className={styles.progressBar}>
      <div
        className={`${styles.progressFill} ${fillClass}`}
        style={{ width: `${Math.min(100, percent)}%` }}
      />
    </div>
  );
};

// ── Inline Flag Form ─────────────────────────────────────────

interface FlagFormProps {
  onSubmit: (description: string, severity: IssueSeverity) => void;
  onCancel: () => void;
  t: (key: string) => string;
}

const InlineFlagForm: React.FC<FlagFormProps> = ({ onSubmit, onCancel, t }) => {
  const [desc, setDesc] = useState("");
  const [severity, setSeverity] = useState<IssueSeverity>("warning");

  const handleSubmit = () => {
    if (!desc.trim()) return;
    onSubmit(desc.trim(), severity);
  };

  return (
    <div className={styles.flagForm}>
      <input
        type="text"
        className={styles.flagInput}
        placeholder={t("digest.flagPlaceholder")}
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
        autoFocus
      />
      <select
        className={styles.flagSeveritySelect}
        value={severity}
        onChange={(e) => setSeverity(e.target.value as IssueSeverity)}
      >
        <option value="blocking">{t("digest.severityBlocking")}</option>
        <option value="warning">{t("digest.severityWarning")}</option>
        <option value="informational">{t("digest.severityInfo")}</option>
      </select>
      <button type="button" className={styles.flagSubmitBtn} onClick={handleSubmit}>
        {t("digest.flagSubmit")}
      </button>
      <button type="button" className={styles.flagCancelBtn} onClick={onCancel}>
        {t("digest.flagCancel")}
      </button>
    </div>
  );
};

// ── Task Row — shared across all RACI types ──────────────────

interface TaskRowProps {
  task: DigestTask;
  raciType: RaciType;
  t: (key: string) => string;
  onUpdatePercent: (taskId: string, value: number) => Promise<void>;
  onMarkComplete: (taskId: string) => Promise<void>;
  onFlagIssue: (taskId: string, desc: string, severity: IssueSeverity) => Promise<void>;
  onAcknowledge: (taskId: string, note?: string) => Promise<void>;
  onRespond: (taskId: string, responseText: string) => Promise<void>;
}

const TaskRow: React.FC<TaskRowProps> = ({
  task,
  raciType,
  t,
  onUpdatePercent,
  onMarkComplete,
  onFlagIssue,
  onAcknowledge,
  onRespond,
}) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(task.percentComplete));
  const [showFlagForm, setShowFlagForm] = useState(false);

  const isComplete = task.percentComplete >= 100;
  const due = dueDateDisplay(task.endDate, t, isComplete);

  const handlePercentSubmit = useCallback(() => {
    const val = Math.max(0, Math.min(100, parseInt(editValue, 10) || 0));
    setEditing(false);
    if (val !== task.percentComplete) {
      onUpdatePercent(task.id, val);
    }
  }, [editValue, task.id, task.percentComplete, onUpdatePercent]);

  const handleFlagSubmit = useCallback(
    (desc: string, severity: IssueSeverity) => {
      onFlagIssue(task.id, desc, severity);
      setShowFlagForm(false);
    },
    [task.id, onFlagIssue]
  );

  const rowClass = `${styles.taskRow} ${
    raciType === "I" && task.acknowledged ? styles.acknowledgedRow : ""
  }`;

  return (
    <>
      <div className={rowClass} style={{ borderInlineStart: `3px solid ${resolveBorderColor(task)}` }}>
        {/* Column 1: Task info */}
        <div className={styles.taskInfo}>
          <div className={styles.taskNameRow}>
            <span className={task.isMilestone ? styles.taskNameMilestone : styles.taskName}>
              {task.taskName}
            </span>
          </div>
          {task.wbsPath && <span className={styles.wbsPath}>{task.wbsPath}</span>}

          {/* Assigned-to chip for A/C items */}
          {task.assignedTo && (
            <span className={styles.assignedToChip}>
              {task.assignedTo.name}
              {task.assignedTo.daysSinceUpdate != null && task.assignedTo.daysSinceUpdate > 3 && (
                <span
                  className={
                    task.assignedTo.daysSinceUpdate > 7
                      ? styles.stalenessAlert
                      : styles.stalenessWarn
                  }
                >
                  ({t("digest.noUpdateDays").replace("{{n}}", String(task.assignedTo.daysSinceUpdate))})
                </span>
              )}
            </span>
          )}

          {/* Consultation badge for C items */}
          {raciType === "C" && (
            <span
              className={`${styles.consultBadge} ${
                task.inputProvided ? styles.consultProvided : task.consultationRequested ? styles.consultRequested : ""
              }`}
            >
              {task.inputProvided
                ? t("digest.inputProvided")
                : task.consultationRequested
                ? t("digest.inputNeeded")
                : ""}
            </span>
          )}

          {/* Change description for I items */}
          {raciType === "I" && task.changeDescription && (
            <span className={styles.changeDescription}>{task.changeDescription}</span>
          )}

          {/* Blockers */}
          {task.blockers.length > 0 && (
            <div className={styles.blockerList}>
              {task.blockers.map((b, i) => (
                <span key={i} className={styles.blockerChip}>
                  {t("digest.blockedBy")} {b.taskName} ({b.ownerName}, {b.percentComplete}%)
                </span>
              ))}
            </div>
          )}

          {/* Issues */}
          {task.issues.length > 0 && (
            <div className={styles.blockerList}>
              {task.issues.map((iss) => (
                <span key={iss.id} className={`${styles.issueChip} ${ISSUE_CLASS[iss.severity] ?? ""}`}>
                  {iss.description}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Column 2: Due date */}
        <span className={`${styles.dueDate} ${due.className}`}>{due.text}</span>

        {/* Column 3: Progress */}
        <div className={styles.progressCell}>
          {raciType !== "I" ? (
            <>
              <ProgressBar percent={task.percentComplete} />
              {editing ? (
                <input
                  type="number"
                  className={styles.progressInput}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onBlur={handlePercentSubmit}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handlePercentSubmit();
                    if (e.key === "Escape") setEditing(false);
                  }}
                  min={0}
                  max={100}
                  autoFocus
                />
              ) : (
                <span
                  className={styles.progressLabel}
                  onClick={() => {
                    if (raciType === "R") {
                      setEditValue(String(task.percentComplete));
                      setEditing(true);
                    }
                  }}
                  style={raciType === "R" ? { cursor: "pointer" } : undefined}
                >
                  {task.percentComplete}%
                </span>
              )}
            </>
          ) : (
            <span className={styles.progressLabel} style={{ flex: 1, textAlign: "center" as const }}>
              —
            </span>
          )}
        </div>

        {/* Column 4: Comment indicator (DEV-720 Phase 1 — gantt_task anchor) */}
        <span className={styles.commentIndicator}>
          <CommentIndicator anchorType="gantt_task" anchorId={task.id} title={task.taskName} />
        </span>

        {/* Column 5: Actions */}
        <div className={styles.actions}>
          {raciType === "R" && !isComplete && (
            <>
              <button
                type="button"
                className={styles.actionBtnDone}
                onClick={() => onMarkComplete(task.id)}
              >
                {t("digest.markDone")}
              </button>
              <button
                type="button"
                className={task.issues.length > 0 ? styles.flagBtnActive : styles.flagBtn}
                onClick={() => setShowFlagForm(!showFlagForm)}
                title={t("digest.flag")}
              >
                <svg viewBox="0 0 24 24" width="16" height="16">
                  {task.issues.length > 0 ? (
                    <path d="M14.4 6l-.24-1.2A2 2 0 0 0 12.2 3H5v18h2v-7h4.2l.24 1.2A2 2 0 0 0 13.4 17H21V6h-6.6z" fill="currentColor" />
                  ) : (
                    <path d="M14.4 6l-.24-1.2A2 2 0 0 0 12.2 3H5v18h2v-7h4.2l.24 1.2A2 2 0 0 0 13.4 17H21V6h-6.6z" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  )}
                </svg>
              </button>
            </>
          )}
          {raciType === "R" && isComplete && (
            <span className={styles.completeBadge}>✓</span>
          )}
          {raciType === "A" && (
            <button
              type="button"
              className={task.issues.length > 0 ? styles.flagBtnActive : styles.flagBtn}
              onClick={() => setShowFlagForm(!showFlagForm)}
              title={t("digest.flag")}
            >
              <svg viewBox="0 0 24 24" width="16" height="16">
                {task.issues.length > 0 ? (
                  <path d="M14.4 6l-.24-1.2A2 2 0 0 0 12.2 3H5v18h2v-7h4.2l.24 1.2A2 2 0 0 0 13.4 17H21V6h-6.6z" fill="currentColor" />
                ) : (
                  <path d="M14.4 6l-.24-1.2A2 2 0 0 0 12.2 3H5v18h2v-7h4.2l.24 1.2A2 2 0 0 0 13.4 17H21V6h-6.6z" fill="none" stroke="currentColor" strokeWidth="1.5" />
                )}
              </svg>
            </button>
          )}
          {raciType === "C" && !task.inputProvided && (
            <button
              type="button"
              className={styles.actionBtnPrimary}
              onClick={() => {
                // Free-text consultation response — persisted as a governance
                // receipt on task_consultations (not a comment). Empty input
                // aborts. DEV-737.
                const raw = typeof window !== "undefined"
                  ? window.prompt(t("digest.respondPrompt"))
                  : "";
                if (raw === null) return;
                const text = raw.trim();
                if (!text) return;
                void onRespond(task.id, text);
              }}
            >
              {t("digest.respond")}
            </button>
          )}
          {raciType === "C" && task.inputProvided && (
            <span
              className={styles.completeBadge}
              title={task.consultationResponseText ?? undefined}
            >
              ✓{task.consultationResponseText ? " ✎" : ""}
            </span>
          )}
          {raciType === "I" && !task.acknowledged && (
            <button
              type="button"
              className={styles.actionBtn}
              onClick={() => {
                // Optional governance note — same window.prompt pattern as
                // Flag Resolution. Cancel aborts; empty OK = acknowledge
                // without note. DEV-736.
                const raw = typeof window !== "undefined"
                  ? window.prompt(t("digest.acknowledgeNotePrompt"))
                  : "";
                if (raw === null) return;
                const note = raw.trim() || undefined;
                void onAcknowledge(task.id, note);
              }}
            >
              {t("digest.acknowledge")}
            </button>
          )}
          {raciType === "I" && task.acknowledged && (
            <span
              className={styles.completeBadge}
              title={task.acknowledgmentNote ?? undefined}
            >
              ✓{task.acknowledgmentNote ? " ✎" : ""}
            </span>
          )}
        </div>
      </div>

      {/* Inline flag form */}
      {showFlagForm && (
        <InlineFlagForm
          onSubmit={handleFlagSubmit}
          onCancel={() => setShowFlagForm(false)}
          t={t}
        />
      )}
    </>
  );
};

// ── Section component ────────────────────────────────────────

interface DigestSectionProps {
  config: SectionConfig;
  tasks: DigestTask[];
  t: (key: string) => string;
  onUpdatePercent: (taskId: string, value: number) => Promise<void>;
  onMarkComplete: (taskId: string) => Promise<void>;
  onFlagIssue: (taskId: string, desc: string, severity: IssueSeverity) => Promise<void>;
  onAcknowledge: (taskId: string, note?: string) => Promise<void>;
  onRespond: (taskId: string, responseText: string) => Promise<void>;
}

const DigestSection: React.FC<DigestSectionProps> = ({
  config,
  tasks,
  t,
  onUpdatePercent,
  onMarkComplete,
  onFlagIssue,
  onAcknowledge,
  onRespond,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  if (tasks.length === 0) return null;

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader} onClick={() => setCollapsed(!collapsed)}>
        <span className={`${styles.sectionBadge} ${config.badgeClass}`}>
          {config.type}
        </span>
        <span className={styles.sectionTitle}>{t(config.titleKey)}</span>
        <span className={styles.sectionCount}>({tasks.length})</span>
        <span className={styles.sectionSubtitle}>{t(config.subtitleKey)}</span>
        <span
          className={`${styles.sectionChevron} ${
            !collapsed ? styles.sectionChevronOpen : ""
          }`}
        >
          ▶
        </span>
      </div>

      {!collapsed && (
        <div className={styles.sectionBody}>
          {tasks.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              raciType={config.type}
              t={t}
              onUpdatePercent={onUpdatePercent}
              onMarkComplete={onMarkComplete}
              onFlagIssue={onFlagIssue}
              onAcknowledge={onAcknowledge}
              onRespond={onRespond}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════

const RoleDigestView: React.FC<RoleDigestViewProps> = ({
  projectGuid,
  roleSlotGuids,
  currentUserGuid,
  debugMode,
}) => {
  const { t } = useAppTranslation("projectTeam");
  const [datePreset, setDatePreset] = useState<DatePreset>("all");

  const dateRange = useMemo(() => getDateRange(datePreset), [datePreset]);

  const {
    rTasks,
    aTasks,
    cTasks,
    iTasks,
    summary,
    loading,
    error,
    updatePercent,
    markComplete,
    flagIssue,
    acknowledge,
    respond,
  } = useRoleDigestData(projectGuid, roleSlotGuids, currentUserGuid, dateRange);

  const taskArrays: Record<RaciType, DigestTask[]> = useMemo(
    () => ({ R: rTasks, A: aTasks, C: cTasks, I: iTasks }),
    [rTasks, aTasks, cTasks, iTasks]
  );

  const DATE_PRESETS: { value: DatePreset; labelKey: string }[] = [
    { value: "today", labelKey: "digest.rangeToday" },
    { value: "week", labelKey: "digest.rangeWeek" },
    { value: "month", labelKey: "digest.rangeMonth" },
    { value: "all", labelKey: "digest.rangeAll" },
  ];

  if (loading) {
    return <div className={styles.loading}>{t("digest.loading")}</div>;
  }

  if (error) {
    return <div className={styles.errorState}>{t("digest.error")}: {error}</div>;
  }

  const totalTasks = summary.totalR + summary.totalA + summary.totalC + summary.totalI;
  if (totalTasks === 0) {
    return <div className={styles.emptyDigest}>{t("digest.empty")}</div>;
  }

  return (
    <div className={styles.digestContainer}>
      {/* Summary strip + date range selector */}
      <SummaryStrip summary={summary} t={t} />

      <div className={styles.dateRangeBar}>
        {DATE_PRESETS.map((p) => (
          <button
            key={p.value}
            type="button"
            className={`${styles.dateRangeBtn} ${
              datePreset === p.value ? styles.dateRangeBtnActive : ""
            }`}
            onClick={() => setDatePreset(p.value)}
          >
            {t(p.labelKey)}
          </button>
        ))}
      </div>

      {/* RACI Sections */}
      {SECTIONS.map((sec) => (
        <DigestSection
          key={sec.type}
          config={sec}
          tasks={taskArrays[sec.type]}
          t={t}
          onUpdatePercent={updatePercent}
          onMarkComplete={markComplete}
          onFlagIssue={flagIssue}
          onAcknowledge={acknowledge}
          onRespond={respond}
        />
      ))}

      {/* Debug output */}
      {debugMode && (
        <pre style={{ fontSize: 10, color: "var(--text-muted)", whiteSpace: "pre-wrap" }}>
          {JSON.stringify(summary, null, 2)}
        </pre>
      )}
    </div>
  );
};

export default RoleDigestView;
